import { getErrorMessage } from "@/lib/error";
import { toastError } from "@/lib/toast-error";
import { useState, useEffect } from "react";
import { useLocation, useParams } from "wouter";
import { 
  useGetDeal, 
  useRefreshDealFromSalesforce,
  useDeleteDeal,
  useListTemplates,
  useSearchChannelManagers,
  usePreviewDealRegistrationForSupplier,
  useSendDealRegistrationForSupplier,
  useGetCopyOnRegPref,
  getGetCopyOnRegPrefQueryKey,
  useSendSessionSummary,
  useListRegistrationJobStatuses,
  useRetryRegistrationSend,
  getListRegistrationJobStatusesQueryKey,
  RegistrationStatus,
  RegistrationJobStatus,
  RegistrationJobStatusStatus,
  ChannelManager,
  Registration,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import { ArrowLeft, Building2, Calendar, RefreshCw, Send, User, Search, ShieldAlert, SkipForward, ChevronLeft, AlertTriangle, Loader2, UserPlus, ExternalLink, Trash2 } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "wouter";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";

interface TemplateFieldDef {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
}

type DealRegistration = Registration & {
  supplierSfContactId?: string;
  mergedFields?: Record<string, unknown>;
  templateFields?: TemplateFieldDef[];
};

interface AlreadySentEntry {
  supplierSfContactId: string;
  supplierAccountName: string;
  supplierContactName: string;
  registrationId: string;
  status: RegistrationStatus;
}

interface AddModeTemplateOption {
  templateId: string;
  templateName: string;
  templateTechnology: string;
  templateFields: TemplateFieldDef[];
  extraFields: Record<string, unknown>;
}

interface AddModeConfig {
  templates: AddModeTemplateOption[];
  alreadySent: AlreadySentEntry[];
}

function buildAddMode(registrations: DealRegistration[]): AddModeConfig | undefined {
  // All persisted registrations represent real supplier contacts — failed sends
  // are deleted by the API, so any registration in this list is a real one.
  // The button should appear and the filter should apply regardless of the
  // registration's current lifecycle status (sent, accepted, bid, no_bid, etc.).
  const regsWithSupplier = registrations.filter(r => r.supplierSfContactId);
  if (regsWithSupplier.length === 0) return undefined;

  // Registrations are returned newest-first (DESC). Walk from oldest to newest
  // so the first occurrence of each template is the original one (matching the
  // pre-existing single-template behavior of using the oldest registration).
  const templates: AddModeTemplateOption[] = [];
  const seenTemplates = new Set<string>();
  for (let i = registrations.length - 1; i >= 0; i--) {
    const reg = registrations[i]!;
    const tplId = reg.templateId ?? "";
    if (!tplId || seenTemplates.has(tplId)) continue;
    seenTemplates.add(tplId);

    const extraFields: Record<string, unknown> = {};
    if (reg.templateFields && reg.mergedFields) {
      for (const field of reg.templateFields) {
        if (reg.mergedFields[field.key] !== undefined) {
          extraFields[field.key] = reg.mergedFields[field.key];
        }
      }
    }

    templates.push({
      templateId: tplId,
      templateName: reg.templateName ?? "Unknown template",
      templateTechnology: reg.technology ?? "",
      templateFields: reg.templateFields ?? [],
      extraFields,
    });
  }

  if (templates.length === 0) return undefined;

  // De-duplicate by supplierSfContactId so each supplier appears once.
  const seen = new Set<string>();
  const alreadySent: AlreadySentEntry[] = [];
  for (const r of regsWithSupplier) {
    if (!seen.has(r.supplierSfContactId!)) {
      seen.add(r.supplierSfContactId!);
      alreadySent.push({
        supplierSfContactId: r.supplierSfContactId!,
        supplierAccountName: r.supplierAccountName ?? "",
        supplierContactName: r.supplierContactName ?? "",
        registrationId: r.id,
        status: r.status as RegistrationStatus,
      });
    }
  }

  return {
    templates,
    alreadySent,
  };
}

export default function DealDetail() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();

  const { data: deal, isLoading, refetch } = useGetDeal(id || "", {
    query: { enabled: !!id, queryKey: ["/api/deals", id] }
  });

  const refreshDeal = useRefreshDealFromSalesforce();

  const handleRefresh = () => {
    if (!id) return;
    refreshDeal.mutate(
      { id },
      {
        onSuccess: () => {
          toast.success("Deal synced from Salesforce");
          refetch();
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to sync deal");
        }
      }
    );
  };

  const addMode = deal?.registrations
    ? buildAddMode(deal.registrations as DealRegistration[])
    : undefined;

  const deleteDeal = useDeleteDeal();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDelete = () => {
    if (!id) return;
    deleteDeal.mutate(
      { id },
      {
        onSuccess: () => {
          toast.success("Deal deleted");
          setLocation("/deals");
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to delete deal");
          setShowDeleteDialog(false);
        },
      }
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Link href="/deals">
            <Button variant="ghost" size="icon" className="shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">Deal Detail</h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
              <p className="text-muted-foreground text-sm sm:text-base">Manage registrations for this opportunity.</p>
              {deal?.sfOpportunityId && (
                <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded border shrink-0">
                  SF: {deal.sfOpportunityId}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshDeal.isPending || isLoading}
          className="w-full sm:w-auto sm:self-auto shrink-0"
          data-testid="button-sync-salesforce"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshDeal.isPending ? 'animate-spin' : ''}`} />
          Sync Salesforce
        </Button>
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto shrink-0 border-destructive/50 text-destructive hover:bg-destructive/10"
              disabled={isLoading}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete this deal?</DialogTitle>
              <DialogDescription>
                This will permanently delete the deal and all associated registrations. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteDeal.isPending}
              >
                {deleteDeal.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                Delete Deal
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <Card>
            <CardHeader><Skeleton className="h-6 w-1/4" /></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-6 w-full" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      ) : deal ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="break-words">{deal.opportunityName}</CardTitle>
                <CardDescription className="flex items-start gap-2 mt-1">
                  <Building2 className="h-4 w-4 mt-0.5 shrink-0" />
                  <span className="break-words min-w-0">{deal.accountName}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                  <div className="min-w-0">
                    <div className="text-xs sm:text-sm text-muted-foreground mb-1">Close Date</div>
                    <div className="font-medium flex items-center gap-2 text-sm sm:text-base">
                      <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="truncate">{formatDate(deal.closeDate) || "—"}</span>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs sm:text-sm text-muted-foreground mb-1">MRR</div>
                    <div className="font-medium text-primary text-sm sm:text-base truncate">{deal.mrr ? formatCurrency(deal.mrr) : "—"}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs sm:text-sm text-muted-foreground mb-1">TCV</div>
                    <div className="font-medium text-sm sm:text-base truncate">{deal.tcv ? formatCurrency(deal.tcv) : "—"}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs sm:text-sm text-muted-foreground mb-1">Last Sync</div>
                    <div className="text-xs sm:text-sm truncate">{formatDateTime(deal.updatedAt)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 sm:mt-8 pt-6 border-t">
                  <div>
                    <div className="text-sm text-muted-foreground mb-2 uppercase tracking-wider font-semibold">Primary Contact</div>
                    <div className="space-y-1">
                      <div className="font-medium">{deal.primaryContactName || "—"}</div>
                      {deal.primaryContactEmail && <div className="text-sm">{deal.primaryContactEmail}</div>}
                      {deal.primaryContactPhone && <div className="text-sm text-muted-foreground">{deal.primaryContactPhone}</div>}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-2 uppercase tracking-wider font-semibold">Owner</div>
                    <div className="space-y-1">
                      <div className="font-medium">{deal.ownerName || "—"}</div>
                      {deal.ownerEmail && <div className="text-sm">{deal.ownerEmail}</div>}
                    </div>
                  </div>
                </div>

                {deal.products && deal.products.length > 0 && (
                  <div className="mt-8 pt-6 border-t">
                    <div className="text-sm text-muted-foreground mb-3 uppercase tracking-wider font-semibold">Products</div>
                    <div className="flex flex-wrap gap-2">
                      {deal.products.map((p, i) => (
                        <div key={i} className="px-3 py-1 bg-secondary text-secondary-foreground rounded-md text-sm font-medium border">
                          {p}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {deal.notes && (
                  <div className="mt-8 pt-6 border-t">
                    <div className="text-sm text-muted-foreground mb-2 uppercase tracking-wider font-semibold">Notes</div>
                    <div className="text-sm whitespace-pre-wrap">{deal.notes}</div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h3 className="text-xl font-bold">Registrations</h3>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 [&_button]:w-full sm:[&_button]:w-auto">
                  {addMode && (
                    <SendDealDialog dealId={deal.id} addMode={addMode} />
                  )}
                  <SendDealDialog dealId={deal.id} />
                </div>
              </div>

              {deal.registrations && deal.registrations.length > 0 ? (
                <RegistrationList
                  dealId={deal.id}
                  registrations={deal.registrations as DealRegistration[]}
                  onOpen={(regId) => setLocation(`/registrations/${regId}`)}
                  onRetried={() => refetch()}
                />
              ) : (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center p-12 text-center text-muted-foreground">
                    <Send className="h-10 w-10 mb-4 opacity-20" />
                    <p className="font-medium text-foreground">No registrations yet</p>
                    <p className="text-sm mt-1 max-w-sm">Send this deal to suppliers to start the registration process.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
          
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">No recent activity.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-destructive font-medium">Deal not found.</div>
      )}
    </div>
  );
}

function JobStatusBadge({ status }: { status: RegistrationJobStatusStatus }) {
  const cls = {
    [RegistrationJobStatusStatus.queued]:
      "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
    [RegistrationJobStatusStatus.running]:
      "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800",
    [RegistrationJobStatusStatus.failed]:
      "bg-red-50 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800",
    [RegistrationJobStatusStatus.cancelled]:
      "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300",
    [RegistrationJobStatusStatus.done]:
      "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800",
  }[status];
  const label = {
    [RegistrationJobStatusStatus.queued]: "Queued",
    [RegistrationJobStatusStatus.running]: "Sending…",
    [RegistrationJobStatusStatus.failed]: "Send failed",
    [RegistrationJobStatusStatus.cancelled]: "Cancelled",
    [RegistrationJobStatusStatus.done]: "Sent",
  }[status];
  return (
    <Badge
      variant="outline"
      className={cls}
      data-testid={`job-status-${status}`}
    >
      {status === RegistrationJobStatusStatus.queued ||
      status === RegistrationJobStatusStatus.running ? (
        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
      ) : null}
      {label}
    </Badge>
  );
}

function RegistrationList({
  dealId,
  registrations,
  onOpen,
  onRetried,
}: {
  dealId: string;
  registrations: DealRegistration[];
  onOpen: (registrationId: string) => void;
  onRetried: () => void;
}) {
  const queryClient = useQueryClient();
  const ids = registrations.map((r) => r.id);
  const idsParam = ids.join(",");

  const queryKey = getListRegistrationJobStatusesQueryKey({ ids: idsParam });
  const { data: jobsData } = useListRegistrationJobStatuses(
    { ids: idsParam },
    {
      query: {
        queryKey,
        enabled: ids.length > 0,
        refetchInterval: (query) => {
          const data = query.state.data as
            | { statuses?: Record<string, RegistrationJobStatus> }
            | undefined;
          const statuses = data?.statuses ?? {};
          const hasActive = Object.values(statuses).some(
            (s) =>
              s.status === RegistrationJobStatusStatus.queued ||
              s.status === RegistrationJobStatusStatus.running,
          );
          return hasActive ? 3000 : false;
        },
        refetchOnWindowFocus: true,
      },
    },
  );

  const statuses: Record<string, RegistrationJobStatus> = jobsData?.statuses ?? {};

  const retryMut = useRetryRegistrationSend();
  const [retryingId, setRetryingId] = useState<string | null>(null);

  // When all jobs settle (no queued/running), refresh the deal so persisted
  // registration statuses (sent/failed) update too.
  const allSettled = Object.values(statuses).every(
    (s) =>
      s.status !== RegistrationJobStatusStatus.queued &&
      s.status !== RegistrationJobStatusStatus.running,
  );
  const [lastSettledRefresh, setLastSettledRefresh] = useState<boolean>(true);
  useEffect(() => {
    if (allSettled && !lastSettledRefresh) {
      onRetried();
      setLastSettledRefresh(true);
    } else if (!allSettled && lastSettledRefresh) {
      setLastSettledRefresh(false);
    }
  }, [allSettled, lastSettledRefresh, onRetried]);

  const handleRetry = (regId: string) => {
    setRetryingId(regId);
    retryMut.mutate(
      { id: regId },
      {
        onSuccess: () => {
          toast.success("Send queued for retry");
          queryClient.invalidateQueries({ queryKey });
          onRetried();
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to retry send");
        },
        onSettled: () => setRetryingId(null),
      },
    );
  };

  return (
    <div className="space-y-3">
      {registrations.map((reg) => {
        const job = statuses[reg.id];
        const showJobBadge =
          job &&
          (job.status === RegistrationJobStatusStatus.queued ||
            job.status === RegistrationJobStatusStatus.running ||
            job.status === RegistrationJobStatusStatus.failed ||
            job.status === RegistrationJobStatusStatus.cancelled);
        const isFailed =
          job?.status === RegistrationJobStatusStatus.failed ||
          job?.status === RegistrationJobStatusStatus.cancelled;
        return (
          <Card
            key={reg.id}
            className="hover-elevate cursor-pointer transition-all"
            onClick={() => onOpen(reg.id)}
            data-testid={`registration-card-${reg.id}`}
          >
            <CardContent className="p-3 sm:p-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm sm:text-base truncate">
                  <span>{reg.supplierAccountName || "Supplier"}</span>
                  <span className="text-muted-foreground"> · {reg.technology}</span>
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span className="flex items-center gap-1 min-w-0">
                    <User className="h-3 w-3 shrink-0" />
                    <span className="truncate">{reg.supplierContactName}</span>
                  </span>
                  <span className="hidden sm:inline">•</span>
                  <span className="shrink-0">{formatDate(reg.createdAt)}</span>
                </div>
                {isFailed && job?.lastError && (
                  <div className="mt-2 text-xs text-red-700 dark:text-red-400 flex items-start gap-1.5">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span className="truncate" title={job.lastError}>
                      {job.lastError}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                {showJobBadge && job ? (
                  <JobStatusBadge status={job.status} />
                ) : (
                  <StatusBadge status={reg.status as RegistrationStatus} />
                )}
                {isFailed && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetry(reg.id);
                    }}
                    disabled={retryingId === reg.id}
                    data-testid={`btn-retry-${reg.id}`}
                  >
                    {retryingId === reg.id ? (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-1 h-3 w-3" />
                    )}
                    Retry
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

type WizardStatus = "pending" | "sent" | "failed" | "skipped";

interface SupplierWizardEntry {
  cm: ChannelManager;
  status: WizardStatus;
  toAddress: string;
  subject: string;
  body: string;
  isDuplicate: boolean;
  overrideDuplicate: boolean;
  errorMessage?: string;
  sentAt?: string;
  registrationId?: string;
  loaded: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function StatusChip({ status }: { status: WizardStatus }) {
  const cls = {
    pending: "bg-muted text-muted-foreground",
    sent: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    failed: "bg-destructive/15 text-destructive",
    skipped: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  }[status];
  const label = status[0]!.toUpperCase() + status.slice(1);
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`} data-testid={`status-chip-${status}`}>
      {label}
    </span>
  );
}

function SendDealDialog({
  dealId,
  addMode,
}: {
  dealId: string;
  addMode?: AddModeConfig;
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"select" | "wizard" | "summary">("select");
  const [mobileTab, setMobileTab] = useState<"compose" | "body">("compose");
  const isMobile = useIsMobile();

  const { data: copyPref } = useGetCopyOnRegPref({
    query: { enabled: open, queryKey: getGetCopyOnRegPrefQueryKey() },
  });
  const [copyModeOverride, setCopyModeOverride] = useState<"off" | "cc" | "bcc" | "default">("default");

  const [templateId, setTemplateId] = useState<string>("");
  const [searchCM, setSearchCM] = useState("");
  const [selectedCMs, setSelectedCMs] = useState<ChannelManager[]>([]);
  const [extraFields, setExtraFields] = useState<Record<string, unknown>>({});
  const [addModeTemplateId, setAddModeTemplateId] = useState<string>(
    addMode?.templates[0]?.templateId ?? "",
  );

  const [entries, setEntries] = useState<SupplierWizardEntry[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [summaryEmailStatus, setSummaryEmailStatus] = useState<"idle" | "pending" | "sent" | "failed">("idle");

  const isAddMode = !!addMode;
  const alreadySentIds = new Set((addMode?.alreadySent ?? []).map(a => a.supplierSfContactId));

  const activeAddTemplate = isAddMode
    ? (addMode!.templates.find(t => t.templateId === addModeTemplateId) ?? addMode!.templates[0]!)
    : undefined;

  const { data: templates } = useListTemplates({ purpose: "registration" }, { query: { enabled: open && !isAddMode, queryKey: ["/api/templates", { purpose: "registration" }] } });
  const { data: cmSearch } = useSearchChannelManagers(
    { q: searchCM },
    { query: { enabled: open && searchCM.length >= 2, queryKey: ["/api/salesforce/channel-managers", { q: searchCM }] } }
  );

  const previewOne = usePreviewDealRegistrationForSupplier();
  const sendOne = useSendDealRegistrationForSupplier();
  const sendSummary = useSendSessionSummary();

  const selectedTemplate = templates?.results.find(t => t.id === templateId);

  const effectiveTemplateId = isAddMode ? (activeAddTemplate?.templateId ?? "") : templateId;
  const effectiveExtraFields: Record<string, unknown> = isAddMode ? (activeAddTemplate?.extraFields ?? {}) : extraFields;

  const reset = () => {
    setStep("select");
    setTemplateId("");
    setSelectedCMs([]);
    setSearchCM("");
    setExtraFields({});
    setAddModeTemplateId(addMode?.templates[0]?.templateId ?? "");
    setEntries([]);
    setCurrentIdx(0);
    setCopyModeOverride("default");
    setSummaryEmailStatus("idle");
  };

  useEffect(() => {
    if (step !== "summary") return;
    const sentIds = entries
      .filter(e => e.status === "sent" && e.registrationId)
      .map(e => e.registrationId!);
    if (sentIds.length === 0) return;
    if (summaryEmailStatus !== "idle") return;
    setSummaryEmailStatus("pending");
    sendSummary.mutate(
      { id: dealId, data: { registrationIds: sentIds } },
      {
        onSuccess: (data) => setSummaryEmailStatus(data.sent ? "sent" : "failed"),
        onError: () => setSummaryEmailStatus("failed"),
      },
    );
  }, [step]);

  const toggleCM = (cm: ChannelManager) => {
    setSelectedCMs(prev => {
      const exists = prev.find(p => p.id === cm.id);
      if (exists) return prev.filter(p => p.id !== cm.id);
      return [...prev, cm];
    });
  };

  const loadPreview = (idx: number, cm: ChannelManager, override?: string) => {
    previewOne.mutate(
      {
        id: dealId,
        data: {
          templateId: effectiveTemplateId,
          supplierContactId: cm.id,
          toAddressOverride: override,
          extraFields: effectiveExtraFields,
        },
      },
      {
        onSuccess: (data) => {
          setEntries(prev => {
            const next = [...prev];
            const e = next[idx];
            if (!e) return prev;
            next[idx] = {
              ...e,
              toAddress: override ?? data.supplierStoredEmail ?? "",
              subject: data.subject,
              body: data.body,
              isDuplicate: data.isDuplicate,
              loaded: true,
            };
            return next;
          });
        },
        onError: (err: unknown) => toastError(err, "Failed to render preview"),
      },
    );
  };

  const startWizard = () => {
    const tplId = effectiveTemplateId;
    if (!tplId || selectedCMs.length === 0) return;
    const initial: SupplierWizardEntry[] = selectedCMs.map(cm => ({
      cm,
      status: "pending",
      toAddress: cm.email ?? "",
      subject: "",
      body: "",
      isDuplicate: false,
      overrideDuplicate: false,
      loaded: false,
    }));
    setEntries(initial);
    setCurrentIdx(0);
    setStep("wizard");
    setMobileTab("compose");
    loadPreview(0, selectedCMs[0]!);
  };

  const goToIdx = (idx: number) => {
    setCurrentIdx(idx);
    const e = entries[idx];
    if (e && !e.loaded) {
      loadPreview(idx, e.cm, e.toAddress || undefined);
    }
  };

  const updateCurrent = (patch: Partial<SupplierWizardEntry>) => {
    setEntries(prev => {
      const next = [...prev];
      const e = next[currentIdx];
      if (!e) return prev;
      next[currentIdx] = { ...e, ...patch };
      return next;
    });
  };

  const advance = () => {
    if (currentIdx < entries.length - 1) {
      setMobileTab("compose");
      goToIdx(currentIdx + 1);
    } else {
      setStep("summary");
    }
  };

  const handleSend = () => {
    const cur = entries[currentIdx];
    if (!cur) return;
    if (!EMAIL_RE.test(cur.toAddress.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    sendOne.mutate(
      {
        id: dealId,
        data: {
          templateId: effectiveTemplateId,
          supplierContactId: cur.cm.id,
          toAddress: cur.toAddress.trim(),
          subject: cur.subject,
          body: cur.body,
          extraFields: effectiveExtraFields,
          overrideDuplicate: cur.overrideDuplicate,
          copyMode: copyModeOverride === "default" ? undefined : copyModeOverride as "off" | "cc" | "bcc",
        },
      },
      {
        onSuccess: (data) => {
          updateCurrent({
            status: "sent",
            sentAt: data.registration.sentAt ?? new Date().toISOString(),
            registrationId: data.registration.id,
            errorMessage: undefined,
          });
          setTimeout(advance, 200);
        },
        onError: (err: unknown) => {
          const apiErr = err as { providerMessage?: string; error?: unknown };
          const msg = apiErr.providerMessage
            ?? (typeof apiErr.error === "string" ? apiErr.error : undefined)
            ?? getErrorMessage(err, "Send failed");
          updateCurrent({ status: "failed", errorMessage: msg });
        },
      },
    );
  };

  const handleSkip = () => {
    updateCurrent({ status: "skipped", errorMessage: undefined });
    setTimeout(advance, 50);
  };

  const handleBack = () => {
    for (let i = currentIdx - 1; i >= 0; i--) {
      if (entries[i]!.status !== "sent") {
        setEntries(prev => {
          const next = [...prev];
          const e = next[i];
          if (e) next[i] = { ...e, status: "pending", errorMessage: undefined };
          return next;
        });
        goToIdx(i);
        return;
      }
    }
  };

  const handleRetry = (idx: number) => {
    setEntries(prev => {
      const next = [...prev];
      const e = next[idx];
      if (e) next[idx] = { ...e, status: "pending", errorMessage: undefined };
      return next;
    });
    setCurrentIdx(idx);
    setSummaryEmailStatus("idle");
    setStep("wizard");
  };

  const closeAndRefresh = () => {
    setOpen(false);
    if (entries.some(e => e.status === "sent")) {
      window.location.reload();
    }
  };

  const cur = entries[currentIdx];
  const canSend = cur && EMAIL_RE.test(cur.toAddress.trim()) && cur.subject.trim() !== "" && cur.body.trim() !== "" && cur.loaded && (!cur.isDuplicate || cur.overrideDuplicate);
  const hasPrevPending = entries.slice(0, currentIdx).some(e => e.status !== "sent");

  const filteredCMResults = cmSearch?.results?.filter(cm => !alreadySentIds.has(cm.id)) ?? [];
  const canStartWizard = !!effectiveTemplateId && selectedCMs.length > 0;

  const dialogTitle = isAddMode
    ? (step === "select" ? "Add Suppliers"
        : step === "wizard" ? `Supplier ${currentIdx + 1} of ${entries.length}`
        : "Send Summary")
    : (step === "select" ? "Send Deal Registration"
        : step === "wizard" ? `Supplier ${currentIdx + 1} of ${entries.length}`
        : "Send Summary");

  const dialogDescription = isAddMode
    ? (step === "select" ? "Select additional suppliers to receive this registration"
        : step === "wizard" ? "Review and send this supplier's email, then move on to the next."
        : "Review the result of each send.")
    : (step === "select" ? "Select a template and suppliers"
        : step === "wizard" ? "Review and send this supplier's email, then move on to the next."
        : "Review the result of each send.");

  return (
    <Dialog open={open} onOpenChange={(val) => {
      if (!val && step === "wizard" && entries.some(e => e.status === "sent")) {
        setStep("summary");
        return;
      }
      setOpen(val);
      if (!val) {
        const hadSends = entries.some(e => e.status === "sent");
        reset();
        if (hadSends) window.location.reload();
      }
    }}>
      <DialogTrigger asChild>
        {isAddMode ? (
          <Button variant="outline" data-testid="button-add-suppliers">
            <UserPlus className="mr-2 h-4 w-4" /> Add Suppliers
          </Button>
        ) : (
          <Button data-testid="button-send-to-suppliers">
            <Send className="mr-2 h-4 w-4" /> Send to Suppliers
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[760px] w-screen h-[100dvh] max-h-[100dvh] sm:w-[calc(100%-2rem)] sm:h-auto sm:max-h-[90vh] sm:rounded-lg rounded-none overflow-hidden flex flex-col p-4 sm:p-6 gap-3 sm:gap-4">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {step === "wizard" && entries.length > 0 && (
          <div className="border-b pb-3 mb-2 flex flex-wrap items-center gap-2" data-testid="wizard-progress">
            {entries.map((e, i) => (
              <button
                key={e.cm.id}
                type="button"
                onClick={() => {
                  if (e.status === "sent") return;
                  goToIdx(i);
                }}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded border ${i === currentIdx ? "border-primary bg-primary/5" : "border-border"} ${e.status === "sent" ? "opacity-70 cursor-default" : "hover:bg-muted/40"}`}
                data-testid={`wizard-step-chip-${i}`}
              >
                <span className="font-medium">{i + 1}.</span>
                <span className="truncate max-w-[120px]">{e.cm.name}</span>
                <StatusChip status={e.status} />
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto py-4 px-1">
          {step === "select" ? (
            <div className="space-y-6">
              {isAddMode && activeAddTemplate ? (
                <div className="p-4 border rounded-lg bg-muted/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                      {addMode!.templates.length > 1 ? "Template (choose one already used on this deal)" : "Template (locked)"}
                    </div>
                    <Badge variant="secondary">{activeAddTemplate.templateTechnology}</Badge>
                  </div>
                  {addMode!.templates.length > 1 ? (
                    <Select value={addModeTemplateId} onValueChange={setAddModeTemplateId}>
                      <SelectTrigger data-testid="select-add-mode-template">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {addMode!.templates.map(t => (
                          <SelectItem key={t.templateId} value={t.templateId} data-testid={`add-mode-template-option-${t.templateId}`}>
                            {t.templateName} ({t.templateTechnology})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="font-medium">{activeAddTemplate.templateName}</div>
                  )}
                  {activeAddTemplate.templateFields.length > 0 && (
                    <div className="pt-3 border-t space-y-2">
                      <div className="text-xs text-muted-foreground font-medium">Pre-filled fields (read-only)</div>
                      <div className="grid grid-cols-2 gap-3">
                        {activeAddTemplate.templateFields.map(field => (
                          <div key={field.key} className="bg-background p-2.5 rounded border">
                            <div className="text-xs text-muted-foreground mb-1">{field.label}</div>
                            <div className="text-sm font-medium">{String(activeAddTemplate.extraFields[field.key] ?? "—")}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : isAddMode ? null : (
                <>
                  <div className="space-y-3">
                    <Label>Technology Template</Label>
                    <Select value={templateId} onValueChange={setTemplateId}>
                      <SelectTrigger data-testid="select-template">
                        <SelectValue placeholder="Select a template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates?.results.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name} ({t.technology})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedTemplate && selectedTemplate.fields && selectedTemplate.fields.length > 0 && (
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                      <h4 className="font-medium text-sm">Template Fields</h4>
                      {selectedTemplate.fields.map(field => (
                        <div key={field.key} className="space-y-2">
                          <Label>{field.label} {field.required && <span className="text-destructive">*</span>}</Label>
                          {field.type === 'textarea' ? (
                            <Textarea
                              value={String(extraFields[field.key] ?? "")}
                              onChange={(e) => setExtraFields({...extraFields, [field.key]: e.target.value})}
                            />
                          ) : field.type === 'select' ? (
                            <Select
                              value={String(extraFields[field.key] ?? "")}
                              onValueChange={(val) => setExtraFields({...extraFields, [field.key]: val})}
                            >
                              <SelectTrigger><SelectValue placeholder={`Select ${field.label}`} /></SelectTrigger>
                              <SelectContent>
                                {field.options?.map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              type={field.type === 'number' ? 'number' : 'text'}
                              value={String(extraFields[field.key] ?? "")}
                              onChange={(e) => setExtraFields({...extraFields, [field.key]: field.type === 'number' ? Number(e.target.value) : e.target.value})}
                            />
                          )}
                          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="space-y-3">
                <Label>Channel Managers</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search channel managers by name or account..."
                    className="pl-9"
                    value={searchCM}
                    onChange={(e) => setSearchCM(e.target.value)}
                    data-testid="input-search-cm"
                  />
                </div>

                {searchCM.length >= 2 && (
                  <div className="border rounded-md mt-2 bg-card shadow-sm">
                    <div className="max-h-40 overflow-y-auto">
                      {filteredCMResults.length > 0 ? (
                        filteredCMResults.map(cm => {
                          const isSelected = selectedCMs.some(s => s.id === cm.id);
                          return (
                            <div
                              key={cm.id}
                              className={`flex items-center gap-3 p-3 border-b last:border-0 cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-primary/5' : ''}`}
                              onClick={() => toggleCM(cm)}
                              data-testid={`cm-result-${cm.id}`}
                            >
                              <Checkbox checked={isSelected} />
                              <div>
                                <div className="font-medium text-sm">{cm.name}</div>
                                <div className="text-xs text-muted-foreground">{cm.accountName} • {cm.email || <em>no email on file</em>}</div>
                              </div>
                            </div>
                          );
                        })
                      ) : cmSearch?.results && cmSearch.results.length > 0 && filteredCMResults.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">All found managers have already received this registration.</div>
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">No managers found.</div>
                      )}
                    </div>

                    {isAddMode && cmSearch?.results && cmSearch.results.some(cm => alreadySentIds.has(cm.id)) && (
                      <div className="border-t bg-muted/20 px-3 py-2">
                        <div className="text-xs text-muted-foreground font-medium mb-2">
                          Already sent ({cmSearch.results.filter(cm => alreadySentIds.has(cm.id)).length})
                        </div>
                        <div className="space-y-1">
                          {cmSearch.results.filter(cm => alreadySentIds.has(cm.id)).map(cm => {
                            const entry = addMode!.alreadySent.find(a => a.supplierSfContactId === cm.id);
                            return (
                              <div key={cm.id} className="flex items-center justify-between text-xs py-0.5">
                                <span className="text-muted-foreground">{cm.name} ({cm.accountName})</span>
                                {entry && (
                                  <Link href={`/registrations/${entry.registrationId}`} onClick={(e) => e.stopPropagation()}>
                                    <span className="text-primary hover:underline flex items-center gap-0.5">
                                      View <ExternalLink className="h-3 w-3" />
                                    </span>
                                  </Link>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {isAddMode && addMode!.alreadySent.length > 0 && searchCM.length < 2 && (
                  <div className="rounded-md border bg-muted/10 px-4 py-3">
                    <div className="text-xs text-muted-foreground font-medium mb-2 uppercase tracking-wider">
                      Already sent ({addMode!.alreadySent.length})
                    </div>
                    <div className="space-y-1.5">
                      {addMode!.alreadySent.map(entry => (
                        <div key={entry.registrationId} className="flex items-center justify-between">
                          <div className="text-sm flex items-center gap-2 min-w-0">
                            <span className="truncate">
                              <span className="font-medium">{entry.supplierContactName}</span>
                              <span className="text-muted-foreground"> · {entry.supplierAccountName}</span>
                            </span>
                            <StatusBadge status={entry.status} />
                          </div>
                          <Link href={`/registrations/${entry.registrationId}`}>
                            <Button variant="ghost" size="sm" className="h-6 text-xs px-2 text-primary">
                              View <ExternalLink className="ml-1 h-3 w-3" />
                            </Button>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCMs.length > 0 && (
                  <div className="mt-4">
                    <Label className="text-xs text-muted-foreground mb-2 block">Selected Recipients ({selectedCMs.length})</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedCMs.map(cm => (
                        <div key={cm.id} className="flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-xs border">
                          <span className="font-medium">{cm.name}</span>
                          <span className="opacity-70">({cm.accountName})</span>
                          <button onClick={() => toggleCM(cm)} className="ml-1 hover:text-destructive">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : step === "wizard" && cur ? (
            <div className="space-y-4">
              <div className="text-sm">
                <div className="font-semibold text-base">{cur.cm.name}</div>
                <div className="text-muted-foreground">{cur.cm.accountName}</div>
              </div>

              {!cur.loaded && previewOne.isPending ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" /> Rendering preview...
                </div>
              ) : isMobile ? (
                <Tabs value={mobileTab} onValueChange={(v) => setMobileTab(v as "compose" | "body")} className="w-full">
                  <TabsList className="grid w-full grid-cols-2" data-testid="wizard-mobile-tabs">
                    <TabsTrigger value="compose" data-testid="wizard-tab-compose">Recipient & Subject</TabsTrigger>
                    <TabsTrigger value="body" data-testid="wizard-tab-body">
                      Body
                      {cur.body.trim() === "" && <span className="ml-1 text-destructive">*</span>}
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="compose" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="wizard-to-m">
                        To <span className="text-destructive">*</span>
                        {!cur.cm.email && (
                          <span className="ml-2 text-xs text-amber-600 font-normal">No email on file — please enter one.</span>
                        )}
                      </Label>
                      <Input
                        id="wizard-to-m"
                        type="email"
                        value={cur.toAddress}
                        onChange={(e) => updateCurrent({ toAddress: e.target.value })}
                        placeholder="recipient@example.com"
                        data-testid="input-to-address"
                        className={!EMAIL_RE.test(cur.toAddress.trim()) ? "border-destructive" : ""}
                      />
                    </div>

                    {cur.isDuplicate && (
                      <div className="flex items-start gap-2 text-sm bg-destructive/10 text-destructive p-3 rounded">
                        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="font-medium">Duplicate registration warning</div>
                          <div>A registration has already been sent to this supplier for this deal.</div>
                          <div className="flex items-center gap-2 mt-2">
                            <Checkbox
                              id="dup-override-m"
                              checked={cur.overrideDuplicate}
                              onCheckedChange={(c) => updateCurrent({ overrideDuplicate: !!c })}
                              data-testid="checkbox-override-duplicate"
                            />
                            <Label htmlFor="dup-override-m" className="text-xs font-medium">
                              Send anyway (override duplicate check)
                            </Label>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="wizard-subject-m">Subject</Label>
                      <Input
                        id="wizard-subject-m"
                        value={cur.subject}
                        onChange={(e) => updateCurrent({ subject: e.target.value })}
                        data-testid="input-subject"
                      />
                    </div>

                    {cur.errorMessage && (
                      <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-sm p-3 rounded" data-testid="send-error-message">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{cur.errorMessage}</span>
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="body" className="space-y-2 mt-4">
                    <Label htmlFor="wizard-body-m">Body</Label>
                    <Textarea
                      id="wizard-body-m"
                      value={cur.body}
                      onChange={(e) => updateCurrent({ body: e.target.value })}
                      rows={10}
                      className="font-mono text-xs"
                      data-testid="textarea-body"
                    />
                  </TabsContent>
                </Tabs>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="wizard-to">
                      To <span className="text-destructive">*</span>
                      {!cur.cm.email && (
                        <span className="ml-2 text-xs text-amber-600 font-normal">No email on file — please enter one.</span>
                      )}
                    </Label>
                    <Input
                      id="wizard-to"
                      type="email"
                      value={cur.toAddress}
                      onChange={(e) => updateCurrent({ toAddress: e.target.value })}
                      placeholder="recipient@example.com"
                      data-testid="input-to-address"
                      className={!EMAIL_RE.test(cur.toAddress.trim()) ? "border-destructive" : ""}
                    />
                  </div>

                  {cur.isDuplicate && (
                    <div className="flex items-start gap-2 text-sm bg-destructive/10 text-destructive p-3 rounded">
                      <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium">Duplicate registration warning</div>
                        <div>A registration has already been sent to this supplier for this deal.</div>
                        <div className="flex items-center gap-2 mt-2">
                          <Checkbox
                            id="dup-override"
                            checked={cur.overrideDuplicate}
                            onCheckedChange={(c) => updateCurrent({ overrideDuplicate: !!c })}
                            data-testid="checkbox-override-duplicate"
                          />
                          <Label htmlFor="dup-override" className="text-xs font-medium">
                            Send anyway (override duplicate check)
                          </Label>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="wizard-subject">Subject</Label>
                    <Input
                      id="wizard-subject"
                      value={cur.subject}
                      onChange={(e) => updateCurrent({ subject: e.target.value })}
                      data-testid="input-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wizard-body">Body</Label>
                    <Textarea
                      id="wizard-body"
                      value={cur.body}
                      onChange={(e) => updateCurrent({ body: e.target.value })}
                      rows={12}
                      className="font-mono text-xs"
                      data-testid="textarea-body"
                    />
                  </div>

                  {cur.errorMessage && (
                    <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-sm p-3 rounded" data-testid="send-error-message">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>{cur.errorMessage}</span>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center gap-3 pt-1 border-t">
                <Label className="text-xs text-muted-foreground shrink-0">Copy me:</Label>
                <Select
                  value={copyModeOverride}
                  onValueChange={(v) => setCopyModeOverride(v as "off" | "cc" | "bcc" | "default")}
                  data-testid="select-copy-mode-override"
                >
                  <SelectTrigger className="h-7 text-xs flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">
                      {copyPref?.enabled
                        ? `Default (${copyPref.mode?.toUpperCase() ?? "BCC"})`
                        : "Default (off)"}
                    </SelectItem>
                    <SelectItem value="off">Off for this send</SelectItem>
                    <SelectItem value="cc">CC (visible to supplier)</SelectItem>
                    <SelectItem value="bcc">BCC (hidden from supplier)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : step === "summary" ? (
            <div className="space-y-3" data-testid="summary-list">
              {summaryEmailStatus === "sent" && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 text-sm px-3 py-2 rounded-md" data-testid="summary-email-sent">
                  <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  A summary was emailed to you.
                </div>
              )}
              {summaryEmailStatus === "failed" && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 rounded-md" data-testid="summary-email-failed">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  We couldn't send the summary email. Your registrations were still delivered successfully.
                </div>
              )}
              {entries.map((e, i) => (
                <div key={e.cm.id} className="border rounded-lg p-3 flex items-start gap-3" data-testid={`summary-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{e.cm.name}</div>
                    <div className="text-xs text-muted-foreground">{e.cm.accountName} • {e.toAddress || e.cm.email || "—"}</div>
                    {e.sentAt && (
                      <div className="text-xs text-muted-foreground mt-1">Sent {formatDateTime(e.sentAt)}</div>
                    )}
                    {e.errorMessage && (
                      <div className="text-xs text-destructive mt-1">{e.errorMessage}</div>
                    )}
                  </div>
                  <StatusChip status={e.status} />
                  {e.status === "failed" && (
                    <Button size="sm" variant="outline" onClick={() => handleRetry(i)} data-testid={`button-retry-${i}`}>
                      Retry
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <DialogFooter className="pt-4 border-t mt-auto">
          {step === "select" ? (
            <Button
              onClick={startWizard}
              disabled={!canStartWizard}
              data-testid="button-start-wizard"
            >
              Start sending
            </Button>
          ) : step === "wizard" ? (
            <div className="flex items-center justify-between w-full gap-2">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={!hasPrevPending}
                data-testid="button-back"
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleSkip} disabled={sendOne.isPending} data-testid="button-skip">
                  <SkipForward className="mr-1 h-4 w-4" /> Skip
                </Button>
                <Button onClick={handleSend} disabled={!canSend || sendOne.isPending} data-testid="button-send-supplier">
                  {sendOne.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
                  {sendOne.isPending ? "Sending..." : "Send"}
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={closeAndRefresh} data-testid="button-close-summary">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
